import { useSuspenseQueries } from '@tanstack/react-query';
import identity from 'lodash.identity';
import isEqual from 'lodash.isequal';
import React, { useEffect } from 'react';
import { Hasher } from '../api/hasher';
import { useMetricTreeQuery } from '../api/metrics/hooks';
import { BackendMetricsQueryService } from '../api/metrics/metrics-query-service';
import { MetricDetails, MetricTree } from '../api/metrics/types';
import { metricTreeToMetricDetailsMap } from '../api/metrics/utils';
import { useTimeRangesQuery } from '../api/timeranges/hooks';
import { BackendTimeRangesService } from '../api/timeranges/service';
import { TimePoint } from '../api/timeranges/types';
import {
  CohortMetricId,
  EmployeeCohortMetricId,
  RegularMetricId,
} from '../api/types-graphql';
import { Filter, Segment } from '../common/components/filter/filterbar/types';
import {
  useAllowedFields,
  useAvailableFilterSections,
  useGlobalFiltersWithPersistenceAndHistoryWrapper,
  useGlobalMovementFilterWithPersistenceAndHistoryWrapper,
  useGlobalSegmentationWithPersistenceAndHistoryWrapper,
} from '../common/components/filter/hooks';
import {
  getInitialRangeTimeSliderState,
  useTimeSliderWithPersistence,
} from '../common/components/timeslider/range-time-slider-hooks';
import {
  getInitialTimePointSelectorState,
  useTimePointSelectorWithPersistence,
} from '../common/components/timeslider/time-point-selector-hooks';
import { startDateField } from '../constants';
import { now } from '../date/date-utils';
import { domainToDefaultFiltersMap } from '../settings/default_filters';
import { DataTypes } from '../types';
import {
  DisplayContext,
  FilterContext,
  FilterMovementContext,
  FilterRecruitmentContext,
  FilterSectionsContext,
  MetricDetailsMapContext,
  MetricsQueryServiceContext,
  MetricTreeContext,
  ResetContext,
  SegmentationLevel1Context,
  SegmentationLevel1RecruitmentContext,
  SegmentationLevel2Context,
  SegmentationLevel2RecruitmentContext,
  SegmentationSectionsContext,
  TimePointSelectorContext,
  TimeRangesContext,
  TimeSliderContext,
  useCurrentUserContext,
  useDomainContext,
  useExecutorRoleContext,
  useGlobalTimeSliderContext,
  useGraphQlRequestServiceContext,
  useIntercomStoreContext,
  useSimulatedRoleContext,
  useUserRolePreferencesSettingsContext,
} from './contexts';
import {
  useGlobalDisplayHandleWithPersistence,
  useGlobalResetHandle,
  useMemoWithDeps,
} from './hooks';

const DebugPrefix = 'DashboardContextProvider';

const PreDependenciesLoader = ({
  children,
}: {
  children: (
    timeRanges: Partial<Record<DataTypes, TimePoint[]>>,
    metricDetailsMap: Record<
      CohortMetricId | RegularMetricId | EmployeeCohortMetricId,
      MetricDetails
    >,
    metricTree: MetricTree
  ) => React.ReactNode;
}) => {
  const graphQlRequestService = useGraphQlRequestServiceContext();
  const domain = useDomainContext();
  const simulatedRole = useSimulatedRoleContext();

  const timeRangeService = useMemoWithDeps(
    `${DebugPrefix} - timeRangeService`,
    () =>
      new BackendTimeRangesService(
        graphQlRequestService,
        domain,
        simulatedRole
      ),
    [domain, Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const timeRangesQuery = useTimeRangesQuery(
    `${DebugPrefix} - TimeRanges`,
    timeRangeService
  );
  const metricTreeQuery = useMetricTreeQuery(
    `${DebugPrefix} - MetricDetailsMap`
  );

  const results = useSuspenseQueries({
    queries: [timeRangesQuery, metricTreeQuery],
  });

  const [timeRanges, metricTree]: [
    Partial<Record<DataTypes, TimePoint[]>> | null | undefined,
    MetricTree | undefined,
  ] = [results[0].data, results[1].data];

  if (!timeRanges) {
    throw new Error('Time ranges are undefined');
  }

  if (!metricTree) {
    throw new Error('MetricTree is undefined');
  }

  const metricDetailsMap = metricTreeToMetricDetailsMap(metricTree);

  if (!metricDetailsMap) {
    throw new Error('Metric details map is undefined');
  }

  return <>{children(timeRanges, metricDetailsMap, metricTree)}</>;
};

const FilterAndSegmentationSectionsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const timeSliderHandle = useGlobalTimeSliderContext();
  const [timeSliderState] = timeSliderHandle;
  const allowedFields = useAllowedFields();

  const filters =
    allowedFields?.filter((f) =>
      [DataTypes.EMPLOYEE, DataTypes.EVALUATION].includes(f.dataType)
    ) ?? [];
  const segments =
    allowedFields?.filter((f) => [DataTypes.EMPLOYEE].includes(f.dataType)) ??
    [];

  const filterSections = useAvailableFilterSections(
    'filter',
    filters,
    timeSliderState
  );
  const segmentationSections = useAvailableFilterSections(
    'segmentation',
    segments.filter((f) => !isEqual(f, startDateField)),
    timeSliderState
  );

  return (
    <FilterSectionsContext.Provider value={filterSections}>
      <SegmentationSectionsContext.Provider value={segmentationSections}>
        {children}
      </SegmentationSectionsContext.Provider>
    </FilterSectionsContext.Provider>
  );
};

// TODO: once we use useContext everywhere we no longer need to use rootStore
export const DashboardContextProviderInner = ({
  children,
  timeRanges,
  metricDetailsMap,
  metricTree,
}: {
  children: React.ReactNode;
  timeRanges: Partial<Record<DataTypes, TimePoint[]>>;
  metricDetailsMap: Record<
    CohortMetricId | RegularMetricId | EmployeeCohortMetricId,
    MetricDetails
  >;
  metricTree: MetricTree;
}) => {
  const graphQlRequestService = useGraphQlRequestServiceContext();
  const domain = useDomainContext();
  const executorRole = useExecutorRoleContext();
  const currentUser = useCurrentUserContext();
  const intercomStore = useIntercomStoreContext();
  const simulatedRole = useSimulatedRoleContext();
  const defaultFilters = domainToDefaultFiltersMap[domain];
  const userRolePreferences = useUserRolePreferencesSettingsContext();

  const employeeTimeRanges: TimePoint[] | undefined =
    timeRanges[DataTypes.EMPLOYEE];
  if (!employeeTimeRanges) {
    console.error('Employee time ranges are undefined or missing', {
      timeRanges,
    });
    throw new Error('Employee time ranges are undefined');
  }

  // Valentin: we need to be careful with intercom. I think intercom can't properly be simulated.
  // We need to decide how to handle it. Currently, we use the user's email (so role simulation does not apply) but the potentially simulated role preferences.
  // We should use either or.
  useEffect(() => {
    intercomStore.applyWindowIntercomSettings(
      userRolePreferences,
      currentUser?.email ?? null
    );
  }, [userRolePreferences, currentUser?.email, intercomStore]);

  const metricsQueryService = useMemoWithDeps(
    `${DebugPrefix} - metricsQueryService domain=${domain}, executorRole=${
      executorRole.id
    }, simulatedRole=${simulatedRole?.id ?? 'null'}`,
    () =>
      new BackendMetricsQueryService(
        graphQlRequestService,
        domain,
        executorRole,
        simulatedRole
      ),
    [domain, Hasher.hash(executorRole), Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const globalDisplayHandle = useGlobalDisplayHandleWithPersistence(domain);
  const [, globalDisplayDispatch] = globalDisplayHandle;

  const globalFiltersHandle =
    useGlobalFiltersWithPersistenceAndHistoryWrapper<Filter>(
      domain,
      defaultFilters ?? []
    );
  const [, globalFilterDispatch] = globalFiltersHandle;

  const globalSegmentationLevel2Handle =
    useGlobalSegmentationWithPersistenceAndHistoryWrapper<Segment>(
      domain,
      [],
      2
    );
  const [, globalSegmentationLevel2Dispatch] = globalSegmentationLevel2Handle;

  const globalSegmentationLevel1Handle =
    useGlobalSegmentationWithPersistenceAndHistoryWrapper<Segment>(
      domain,
      [],
      1,
      undefined,
      (items: Filter[]) =>
        items.length === 0
          ? globalSegmentationLevel2Dispatch({ type: 'reset' })
          : identity
    );
  const [, globalSegmentationLevel1Dispatch] = globalSegmentationLevel1Handle;

  const globalFiltersRecruitmentHandle =
    useGlobalFiltersWithPersistenceAndHistoryWrapper<Filter>(
      domain,
      [],
      'recruitment'
    );
  const [, globalFilterRecruitmentDispatch] = globalFiltersRecruitmentHandle;

  const globalSegmentationLevel2RecruitmentHandle =
    useGlobalSegmentationWithPersistenceAndHistoryWrapper<Segment>(
      domain,
      [],
      2,
      'recruitment'
    );
  const [, globalSegmentationLevel2RecruitmentDispatch] =
    globalSegmentationLevel2RecruitmentHandle;

  const globalSegmentationLevel1RecruitmentHandle =
    useGlobalSegmentationWithPersistenceAndHistoryWrapper<Segment>(
      domain,
      [],
      1,
      'recruitment',
      (items: Filter[]) =>
        items.length === 0
          ? globalSegmentationLevel2RecruitmentDispatch({ type: 'reset' })
          : identity
    );
  const [, globalSegmentationLevel1RecruitmentDispatch] =
    globalSegmentationLevel1RecruitmentHandle;

  const globalFiltersMovementHandle =
    useGlobalMovementFilterWithPersistenceAndHistoryWrapper<Filter>(
      domain,
      defaultFilters ?? []
    );

  const initialTimeSliderState = getInitialRangeTimeSliderState(
    employeeTimeRanges,
    now()
  );
  const globalTimeSliderHandle = useTimeSliderWithPersistence(
    domain,
    initialTimeSliderState
  );
  const [, globalTimeSliderDispatch] = globalTimeSliderHandle;

  const initialTimePointSelectorState =
    getInitialTimePointSelectorState(employeeTimeRanges);
  const timePointSelectorHandle = useTimePointSelectorWithPersistence(
    domain,
    initialTimePointSelectorState
  );

  const [, timePointSelectorDispatch] = timePointSelectorHandle;

  const globalResetHandle = useGlobalResetHandle([
    () => globalFilterDispatch({ type: 'reset' }),
    () => globalSegmentationLevel1Dispatch({ type: 'reset' }),
    () => globalSegmentationLevel2Dispatch({ type: 'reset' }),
    () => globalFilterRecruitmentDispatch({ type: 'reset' }),
    () => globalSegmentationLevel1RecruitmentDispatch({ type: 'reset' }),
    () => globalSegmentationLevel2RecruitmentDispatch({ type: 'reset' }),
    () => globalDisplayDispatch({ type: 'reset' }),
    () => globalTimeSliderDispatch({ type: 'reset' }),
    () => timePointSelectorDispatch({ type: 'reset' }),
  ]);

  return (
    <MetricsQueryServiceContext.Provider value={metricsQueryService}>
      <ResetContext.Provider value={globalResetHandle}>
        <TimeRangesContext.Provider value={timeRanges}>
          <TimeSliderContext.Provider value={globalTimeSliderHandle}>
            <TimePointSelectorContext.Provider value={timePointSelectorHandle}>
              <FilterContext.Provider value={globalFiltersHandle}>
                <SegmentationLevel1Context.Provider
                  value={globalSegmentationLevel1Handle}
                >
                  <SegmentationLevel2Context.Provider
                    value={globalSegmentationLevel2Handle}
                  >
                    <FilterRecruitmentContext.Provider
                      value={globalFiltersRecruitmentHandle}
                    >
                      <SegmentationLevel1RecruitmentContext.Provider
                        value={globalSegmentationLevel1RecruitmentHandle}
                      >
                        <SegmentationLevel2RecruitmentContext.Provider
                          value={globalSegmentationLevel2RecruitmentHandle}
                        >
                          <FilterMovementContext.Provider
                            value={globalFiltersMovementHandle}
                          >
                            <MetricDetailsMapContext.Provider
                              value={metricDetailsMap}
                            >
                              <MetricTreeContext.Provider value={metricTree}>
                                <DisplayContext.Provider
                                  value={globalDisplayHandle}
                                >
                                  <FilterAndSegmentationSectionsContextProvider>
                                    {children}
                                  </FilterAndSegmentationSectionsContextProvider>
                                </DisplayContext.Provider>
                              </MetricTreeContext.Provider>
                            </MetricDetailsMapContext.Provider>
                          </FilterMovementContext.Provider>
                        </SegmentationLevel2RecruitmentContext.Provider>
                      </SegmentationLevel1RecruitmentContext.Provider>
                    </FilterRecruitmentContext.Provider>
                  </SegmentationLevel2Context.Provider>
                </SegmentationLevel1Context.Provider>
              </FilterContext.Provider>
            </TimePointSelectorContext.Provider>
          </TimeSliderContext.Provider>
        </TimeRangesContext.Provider>
      </ResetContext.Provider>
    </MetricsQueryServiceContext.Provider>
  );
};

export const DashboardContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <PreDependenciesLoader>
      {(timeRanges, metricDetailsMap, metricTree) => (
        <DashboardContextProviderInner
          timeRanges={timeRanges}
          metricDetailsMap={metricDetailsMap}
          metricTree={metricTree}
        >
          {children}
        </DashboardContextProviderInner>
      )}
    </PreDependenciesLoader>
  );
};
